import { motion } from 'motion/react';
import { ArrowUp } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { SiteFooterProps, FooterMenuItemData, FooterColumnData } from '@/types/theme';

type FooterColumn = {
	label: string;
	links: FooterMenuItemData[];
};

function buildColumns(items: FooterMenuItemData[], labels: string[]): FooterColumn[] {
	const topLevel = items.filter((i) => !i.parent || i.parent === 0);
	const children = items.filter((i) => i.parent && i.parent !== 0);

	if (items.length === 0) return [];

	if (children.length === 0) {
		const chunkSize = Math.max(1, Math.ceil(topLevel.length / labels.length));
		return labels
			.map((label, index) => ({
				label,
				links: topLevel.slice(index * chunkSize, (index + 1) * chunkSize),
			}))
			.filter((column) => column.links.length > 0);
	}

	return topLevel.map((col, index) => ({
		label: labels[index] || col.title,
		links: children.filter((c) => c.parent === col.id),
	}));
}

export function SiteFooter({
	siteName,
	siteUrl,
	customLogoUrl,
	customLogoAlt,
	tagline,
	footerMenuItems = [],
	footerColumns = [],
	footerCopyright,
	footerCol1Label,
	footerCol2Label,
	footerCol3Label,
	footerCol4Label,
	footerClasses,
}: SiteFooterProps) {
	const brandUrl = siteUrl || '/';
	const initial  = (siteName ?? 'G').charAt(0).toUpperCase();
	const year     = new Date().getFullYear();
	const copyright = footerCopyright || `© ${year} ${siteName || 'Gurdwara Shahidan Char Sahibzade'}. All rights reserved.`;
	const footerLabels = [
		footerCol1Label || 'Worship',
		footerCol2Label || 'Programs',
		footerCol3Label || 'Community',
		footerCol4Label || 'Connect',
	];

	const columns = buildColumns(footerMenuItems, footerLabels);

	const fallbackColumns = [
		{
			label: footerLabels[0],
			links: [
				{ id: 1,  parent: 0, title: 'Prayer Times',    url: '#' },
				{ id: 2,  parent: 0, title: 'Nitnem',          url: '#' },
				{ id: 3,  parent: 0, title: 'Divan',           url: '#' },
				{ id: 4,  parent: 0, title: 'Hukamnama',       url: '#' },
			],
		},
		{
			label: footerLabels[1],
			links: [
				{ id: 5,  parent: 0, title: 'Youth Learning',  url: '#' },
				{ id: 6,  parent: 0, title: 'Punjabi Classes', url: '#' },
				{ id: 7,  parent: 0, title: 'Sikh Studies',    url: '#' },
				{ id: 8,  parent: 0, title: 'Camps',           url: '#' },
			],
		},
		{
			label: footerLabels[2],
			links: [
				{ id: 9,  parent: 0, title: 'Langar',          url: '#' },
				{ id: 10, parent: 0, title: 'Seva',            url: '#' },
				{ id: 11, parent: 0, title: 'Events',          url: '#' },
				{ id: 12, parent: 0, title: 'Donate',          url: '#' },
			],
		},
		{
			label: footerLabels[3],
			links: [
				{ id: 13, parent: 0, title: 'Contact',         url: '#' },
				{ id: 14, parent: 0, title: 'Location',        url: '#' },
				{ id: 15, parent: 0, title: 'Instagram',       url: '#' },
				{ id: 16, parent: 0, title: 'Facebook',        url: '#' },
			],
		},
	];

	const displayColumns: Array<FooterColumnData | FooterColumn> =
		footerColumns.length > 0 ? footerColumns : columns.length > 0 ? columns : fallbackColumns;

	const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

	return (
		<footer className={cn('relative overflow-hidden border-t border-border/60 bg-card', footerClasses)}>
			{/* Atmospheric bottom-left glow */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute bottom-0 left-0 h-[360px] w-[360px] -translate-x-1/3 translate-y-1/3 rounded-full bg-accent/[0.045] blur-[110px]"
			/>

			{/* Top edge highlight */}
			<div
				aria-hidden="true"
				className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/40 to-transparent"
			/>

			<div className="relative mx-auto max-w-6xl px-6 md:px-12 lg:px-16">

				{/* ── Brand statement row ──────────────────────── */}
				<motion.div
					initial={{ opacity: 0, y: 16 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.06 }}
					transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
					className="flex flex-col gap-5 border-b border-border/50 py-12 sm:flex-row sm:items-center sm:justify-between"
				>
					{/* Logo + name */}
					<a
						href={brandUrl}
						className="group inline-flex items-center gap-3.5"
						aria-label={siteName || 'Home'}
					>
						{customLogoUrl ? (
							<img
								src={customLogoUrl}
								alt={customLogoAlt || siteName || ''}
								className="h-9 w-9 rounded-full object-cover ring-1 ring-black/10 transition-transform duration-300 group-hover:scale-105"
							/>
						) : (
							<span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-accent text-[11px] font-bold uppercase tracking-[0.18em] text-accent-foreground ring-1 ring-accent/30 transition-transform duration-300 group-hover:scale-105">
								{initial}
							</span>
						)}
						<span className="text-[13px] font-semibold tracking-[-0.005em] text-foreground/80 transition-colors group-hover:text-foreground">
							{siteName || 'Gurdwara Shahidan Char Sahibzade'}
						</span>
					</a>

					{/* Tagline */}
					{tagline && (
						<p className="max-w-xs text-[13px] leading-relaxed text-foreground/38 sm:text-right">
							{tagline}
						</p>
					)}
				</motion.div>

				{/* ── Link columns ─────────────────────────────── */}
				<motion.div
					initial={{ opacity: 0, y: 18 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.06 }}
					transition={{ duration: 0.65, delay: 0.06, ease: [0.16, 1, 0.3, 1] }}
					className="grid grid-cols-2 gap-x-8 gap-y-12 py-14 sm:grid-cols-4"
				>
					{displayColumns.map((col) => (
						<div
							key={col.label}
							className={('customClassName' in col && col.customClassName) ? col.customClassName : undefined}
						>
							{/* Column heading */}
							<p className={cn(
								'flex items-center gap-2 text-[9.5px] font-semibold uppercase tracking-[0.46em] text-foreground/32',
								'labelClassName' in col ? col.labelClassName : undefined,
							)}>
								<span aria-hidden="true" className="h-px w-4 shrink-0 bg-accent/50" />
								{col.label}
							</p>

							{/* Links */}
							<ul className="mt-5 space-y-3">
								{col.links.map((link) => (
									<li key={link.id}>
										<a
											href={link.url}
											target={link.target || undefined}
											rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
											className={cn(
												'group inline-flex items-center gap-0 text-[13px] text-foreground/42 transition-colors duration-200 hover:text-foreground',
												link.customClassName,
											)}
										>
											{/* Animated underscore */}
											<span className="relative">
												{link.title}
												<span
													aria-hidden="true"
													className="absolute -bottom-px left-0 h-px w-0 bg-accent/60 transition-all duration-300 group-hover:w-full"
												/>
											</span>
										</a>
									</li>
								))}
							</ul>
						</div>
					))}
				</motion.div>
			</div>

			{/* ── Bottom bar ───────────────────────────────────── */}
			<div className="relative border-t border-border/50">
				<div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-12 lg:px-16">
					<p className="text-[11.5px] text-foreground/30">{copyright}</p>

					<button
						type="button"
						onClick={scrollTop}
						aria-label="Back to top"
						className="group inline-flex h-7 w-7 items-center justify-center rounded-full border border-border/60 text-foreground/25 transition-all duration-200 hover:border-accent/50 hover:text-accent"
					>
						<ArrowUp className="h-3 w-3 transition-transform duration-200 group-hover:-translate-y-0.5" />
					</button>
				</div>

				{/* Bottom gold centre dot */}
				<div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-1">
					<div className="h-0.5 w-0.5 rounded-full bg-accent/30" aria-hidden="true" />
				</div>
			</div>
		</footer>
	);
}
